import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SportChips, { useSportNames } from "../../components/SportChips";
import toast from "react-hot-toast";
import api from "../../services/api";
import { getUsers, deleteUser, type User } from "../../services/users";
import type { Club } from "../../services/clubs";
import LoginAsButton from "../../components/LoginAsButton";
import Pagination, { usePagination } from "../../components/Pagination";
import AdminPageHeader, { AdminButton } from "../../components/admin/AdminPageHeader";
import { formatDate } from "../../lib/dateFormat";

export default function Coaches() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [coaches, setCoaches] = useState<User[]>([]);
  const [clubs, setClubs] = useState<Club[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [clubFilter, setClubFilter] = useState("");
  const [sportFilter, setSportFilter] = useState("");
  // Clubs store sport IDs; the filter values are those IDs, and this turns them
  // into the names a human is choosing between.
  const sportName = useSportNames();
  const [cityFilter, setCityFilter] = useState("");
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);

  useEffect(() => {
    fetchCoaches();
    (async () => {
      try {
        const { data } = await api.get<Club[]>("/clubs");
        const flat = Array.isArray(data)
          ? data.map((row: any) => row?.club ?? row).filter(Boolean)
          : [];
        setClubs(flat);
      } catch (err) {
        console.error("Failed to load clubs for filters:", err);
      }
    })();
  }, []);

  const fetchCoaches = async () => {
    try {
      setLoading(true);
      // pageSize=500 — see Athletes.tsx comment; the default 20
      // truncated big clubs and any client-side filter would come
      // up short.
      const response = await getUsers({ role: "coach", pageSize: 500 });
      setCoaches(response.data);
    } catch (error) {
      console.error("Failed to fetch coaches:", error);
      toast.error(t("coaches.error_load", "Nu s-au putut încărca antrenorii"));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (userId: string) => {
    if (deleteConfirmId === userId) {
      // Second click - proceed with delete
      handleDeleteConfirm(userId);
    } else {
      // First click - show confirmation
      setDeleteConfirmId(userId);
      setTimeout(() => setDeleteConfirmId(null), 3000); // Reset after 3 seconds
    }
  };

  const handleDeleteConfirm = async (userId: string) => {
    try {
      await deleteUser(userId);
      toast.success(t("coaches.deleted", "Antrenor șters cu succes!"));
      setDeleteConfirmId(null);
      fetchCoaches();
    } catch (error: any) {
      console.error("Failed to delete coach:", error);
      toast.error(error.response?.data?.error || t("coaches.error_delete", "Nu s-a putut șterge antrenorul"));
    }
  };

  // Bulk selection handlers
  const toggleSelectAll = () => {
    if (selectedIds.size === filteredCoaches.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredCoaches.map(c => c.id)));
    }
  };

  const toggleSelect = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;

    setBulkDeleting(true);
    let successCount = 0;
    let errorCount = 0;

    for (const id of Array.from(selectedIds)) {
      try {
        await deleteUser(id);
        successCount++;
      } catch (error) {
        errorCount++;
      }
    }

    setBulkDeleting(false);
    setShowBulkConfirm(false);
    setSelectedIds(new Set());

    if (successCount > 0) {
      toast.success(t("coaches.bulk_deleted", "{{count}} antrenor(i) șters(i) cu succes!", { count: successCount }));
    }
    if (errorCount > 0) {
      toast.error(t("coaches.bulk_delete_error", "{{count}} antrenor(i) nu au putut fi șterși", { count: errorCount }));
    }

    fetchCoaches();
  };

  const sportOptions = useMemo(
    () =>
      Array.from(
        new Set(clubs.flatMap((c) => c.sports || []).filter(Boolean))
      ).sort(),
    [clubs]
  );
  const cityOptions = useMemo(
    () =>
      Array.from(
        new Set(clubs.map((c) => c.city).filter((v): v is string => !!v))
      ).sort(),
    [clubs]
  );

  const filteredCoaches = coaches.filter((coach) => {
    const searchLower = searchTerm.toLowerCase();
    const searchMatch =
      coach.full_name?.toLowerCase().includes(searchLower) ||
      coach.email.toLowerCase().includes(searchLower) ||
      coach.coach_profiles?.club?.name?.toLowerCase().includes(searchLower);
    if (!searchMatch) return false;

    if (clubFilter && coach.coach_profiles?.club?.id !== clubFilter) return false;
    if (cityFilter && coach.coach_profiles?.club?.city !== cityFilter) return false;
    if (sportFilter) {
      const coachSports = coach.coach_profiles?.club?.sports || [];
      if (!coachSports.includes(sportFilter)) return false;
    }
    return true;
  });

  // Lista vine întreagă de la server (pageSize=500), deci paginăm în client.
  // Selecția în masă rămâne peste TOT ce trece filtrele, nu doar peste pagina
  // vizibilă — altfel „selectează tot" ar însemna altceva pe fiecare pagină.
  const { page, setPage, pageSize, total, visible: pagedCoaches } = usePagination(filteredCoaches);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="flex items-center gap-3">
          <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-slate-600 font-medium">{t("common.loading", "Se încarcă...")}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="mx-auto max-w-[1500px]">
        {/* Header */}
        <AdminPageHeader
          eyebrow="Utilizatori și membri"
          title={t("coaches.title", "Antrenori")}
          subtitle={<>{t("coaches.subtitle", "Gestionare antrenori")} ({filteredCoaches.length})</>}
          icon={
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87M16 3.13a4 4 0 010 7.75M8 3.13a4 4 0 000 7.75" />
            </svg>
          }
          actions={
            <AdminButton
              onClick={() => navigate("/admin/users")}
              icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>}
            >
              Adaugă Antrenor Nou
            </AdminButton>
          }
        />

        {/* Search + filters */}
        <div className="mb-6 bg-white rounded-2xl shadow-sm border border-slate-200 p-4 space-y-3">
          <div className="relative">
            <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder={t("coaches.search_placeholder", "Caută antrenor după nume, email sau club...")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <select
              value={clubFilter}
              onChange={(e) => setClubFilter(e.target.value)}
              className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            >
              <option value="">Toate cluburile</option>
              {clubs
                .slice()
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
            </select>
            <select
              value={sportFilter}
              onChange={(e) => setSportFilter(e.target.value)}
              className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            >
              <option value="">Toate sporturile</option>
              {sportOptions.map((s) => (
                <option key={s} value={s}>{sportName(s)}</option>
              ))}
            </select>
            <select
              value={cityFilter}
              onChange={(e) => setCityFilter(e.target.value)}
              className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            >
              <option value="">Toate orașele</option>
              {cityOptions.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          {(clubFilter || sportFilter || cityFilter) && (
            <button
              onClick={() => { setClubFilter(""); setSportFilter(""); setCityFilter(""); }}
              className="text-xs text-slate-500 hover:text-slate-700 underline"
            >
              Șterge filtrele
            </button>
          )}
        </div>

        {/* Bulk Actions Bar */}
        {selectedIds.size > 0 && (
          <div className="mb-4 bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center justify-between">
            <span className="text-blue-700 font-medium">
              {t("coaches.selected_count", "{{count}} antrenor(i) selectat(i)", { count: selectedIds.size })}
            </span>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSelectedIds(new Set())}
                className="px-4 py-2 text-slate-600 hover:text-slate-800 font-medium transition-colors"
              >
                {t("coaches.deselect", "Deselectează")}
              </button>
              <button
                onClick={() => setShowBulkConfirm(true)}
                className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                {t("coaches.delete_selected", "Șterge Selectate")}
              </button>
            </div>
          </div>
        )}

        {/* Bulk Delete Confirmation Modal */}
        {showBulkConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-xl">
              <h3 className="text-xl font-bold text-slate-800 mb-2">{t("coaches.confirm_delete_title", "Confirmare Ștergere")}</h3>
              <p className="text-slate-600 mb-6">
                {t("coaches.confirm_delete_msg", "Ești sigur că vrei să ștergi {{count}} antrenor(i)? Această acțiune nu poate fi anulată.", { count: selectedIds.size })}
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowBulkConfirm(false)}
                  disabled={bulkDeleting}
                  className="px-4 py-2 text-slate-600 hover:text-slate-800 font-medium transition-colors disabled:opacity-50"
                >
                  {t("common.cancel", "Anulează")}
                </button>
                <button
                  onClick={handleBulkDelete}
                  disabled={bulkDeleting}
                  className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  {bulkDeleting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      {t("coaches.deleting", "Se șterge...")}
                    </>
                  ) : (
                    t("coaches.confirm_delete", "Confirmă Ștergerea")
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Coaches Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-4 text-left">
                    <input
                      type="checkbox"
                      checked={filteredCoaches.length > 0 && selectedIds.size === filteredCoaches.length}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                    />
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    {t("common.name", "Nume")}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    {t("coaches.sports", "Sporturi")}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    {t("coaches.athlete_count", "Câți Sportivi")}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    {t("coaches.created", "Creat")}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    {t("common.actions", "Acțiuni")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredCoaches.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-slate-500">
                      <div className="flex flex-col items-center gap-2">
                        <svg className="w-12 h-12 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <span>{t("coaches.no_coaches", "Nu există antrenori înregistrați")}</span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  pagedCoaches.map((coach) => (
                    <tr
                      key={coach.id}
                      className={`hover:bg-slate-50 transition-colors duration-150 ${selectedIds.has(coach.id) ? 'bg-blue-50' : ''}`}
                    >
                      {/* Checkbox */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(coach.id)}
                          onChange={() => toggleSelect(coach.id)}
                          className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                        />
                      </td>
                      {/* Name - Clickable, with club + city underneath */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => navigate(`/admin/users/${coach.id}`)}
                          className="block text-indigo-600 hover:text-indigo-700 font-medium hover:underline transition-colors"
                        >
                          {coach.full_name || coach.email}
                        </button>
                        {(coach.coach_profiles?.club?.name || coach.coach_profiles?.club?.city) && (
                          <div className="text-xs text-slate-400 mt-0.5">
                            {[coach.coach_profiles?.club?.name, coach.coach_profiles?.club?.city].filter(Boolean).join(" · ")}
                          </div>
                        )}
                      </td>

                      {/* Sports */}
                      <td className="px-6 py-4">
                        <SportChips
                          values={coach.coach_profiles?.club?.sports}
                          chipClassName="px-2 py-1 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-medium"
                        />
                      </td>

                      {/* Athlete Count */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-sm font-semibold">
                          {coach.coach_profiles?.athlete_count || 0}
                        </span>
                      </td>

                      {/* Created At */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-slate-500 text-sm">
                          {formatDate(new Date(coach.created_at))}
                        </span>
                      </td>

                      {/* Actions - Login-as + Delete */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <LoginAsButton userId={coach.id} compact />
                          <button
                            onClick={() => handleDeleteClick(coach.id)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 ${
                              deleteConfirmId === coach.id
                                ? "bg-red-600 text-white hover:bg-red-700 shadow-lg shadow-red-500/25"
                                : "bg-red-50 text-red-600 hover:bg-red-100"
                            }`}
                          >
                            {deleteConfirmId === coach.id ? t("coaches.confirm_delete", "Confirmă Ștergere") : t("common.delete", "Șterge")}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <Pagination page={page} pageSize={pageSize} total={total} onPageChange={setPage} />
        </div>
      </div>
    </div>
  );
}
